import {
  type ReactNode,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { InternationalPhoneData, type Address } from '@electricjs/arc';

import { internationalPhoneValidationSchema } from '@common';

import { emailRegex } from '@/constants/email';

import { Product } from '@/types/products';
import { createAddressValidationSchema } from '@/components/AddressValidation/helpers';

export type FirstStepFormData = {
  recipientId: string;
  address: Address;
  phone: InternationalPhoneData;
  email: string;
};

export type SecondStepFormData = {
  selectedHardware?: Product;
  targetDeliveryDate?: Date;
};

type OtherCountryFlowContextType = {
  otherCountryFlowFirstStepForm?: UseFormReturn<FirstStepFormData>;
  otherCountryFlowSecondStepForm?: UseFormReturn<SecondStepFormData>;
  otherCountryFlowStep: number;
  setOtherCountryFlowStep: (step: number) => void;
};

const OtherCountryFlowContext = createContext<OtherCountryFlowContextType>({
  otherCountryFlowFirstStepForm: undefined,
  otherCountryFlowSecondStepForm: undefined,
  otherCountryFlowStep: 1,
  setOtherCountryFlowStep: () => {},
});

type OtherCountryFlowProviderProps = {
  children: ReactNode;
};

export const OtherCountryFlowProvider = (
  props: OtherCountryFlowProviderProps
) => {
  const { t } = useTranslation('storefront');

  const [otherCountryFlowStep, setOtherCountryFlowStep] = useState(1);

  const firstStepFormSchema = yup.object<FirstStepFormData>().shape({
    recipientId: yup
      .string()
      .required(t('international.firstStep.errors.fieldRequired')),
    address: createAddressValidationSchema(true),
    phone: yup
      .object<InternationalPhoneData>()
      .shape({
        phoneNumber: internationalPhoneValidationSchema({
          required: true,
        }),
        country: yup.string(),
      })
      .test(
        'phone-number-required',
        t('international.firstStep.errors.phoneRequired'),
        value => Boolean(value?.phoneNumber)
      ), // the test must be applied at the object level in order to be shown in the component
    email: yup
      .string()
      .matches(emailRegex, t('international.firstStep.errors.invalidEmail'))
      .required(t('international.firstStep.errors.fieldRequired')),
  });

  const firstStepDefaultValues: FirstStepFormData = {
    recipientId: '',
    address: {
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      country: '',
      zip: '',
    },
    phone: {
      phoneNumber: '',
      country: '',
    },
    email: '',
  };

  const otherCountryFlowFirstStepForm = useForm<FirstStepFormData>({
    resolver: yupResolver(firstStepFormSchema),
    defaultValues: firstStepDefaultValues,
    mode: 'onTouched',
  });

  const secondStepFormSchema = yup.object<SecondStepFormData>().shape({
    selectedHardware: yup
      .object()
      .required(t('international.secondStep.errors.fieldRequired')),
    targetDeliveryDate: yup
      .date()
      .required(t('international.secondStep.errors.fieldRequired')),
  });

  const secondStepDefaultValues: SecondStepFormData = {
    selectedHardware: undefined,
    targetDeliveryDate: undefined,
  };

  const otherCountryFlowSecondStepForm = useForm<SecondStepFormData>({
    resolver: yupResolver(secondStepFormSchema),
    defaultValues: secondStepDefaultValues,
    mode: 'onTouched',
  });

  return (
    <OtherCountryFlowContext.Provider
      value={{
        otherCountryFlowFirstStepForm,
        otherCountryFlowSecondStepForm,
        otherCountryFlowStep,
        setOtherCountryFlowStep: useCallback(
          step => setOtherCountryFlowStep(step),
          []
        ),
      }}>
      {props.children}
    </OtherCountryFlowContext.Provider>
  );
};

export const useOtherCountryFlow = () => {
  const {
    otherCountryFlowFirstStepForm,
    otherCountryFlowSecondStepForm,
    otherCountryFlowStep,
    setOtherCountryFlowStep,
  } = useContext(OtherCountryFlowContext);

  return {
    otherCountryFlowFirstStepForm,
    otherCountryFlowSecondStepForm,
    otherCountryFlowStep,
    setOtherCountryFlowStep,
  };
};
